import React, { useState } from "react";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "../../../../shared/hooks/use-toast";
import { Button } from "../../../../shared/ui/button";
import { DynamicForm } from "../../../../shared/components/DynamicForm";
import { useNavigate } from "react-router-dom";
import { forgotPassword } from "@features/auth/api";
import ImageSection from "./ImageSection";

const ForgotPasswordForm: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const navigate = useNavigate();

  // Define form fields for forgot password
  const forgotPasswordFields: FormField[] = [
    {
      name: "email",
      type: "email",
      label: "Email Address",
      required: true,
      placeholder: "Enter your email",
      defaultValue: "",
      pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
      fullWidth: true,
      classes: {
        helperText: "text-xs text-gray-500 mt-1",
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-gray-50",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];

  // Create form using FormService
  const form = FormService.createForm({
    fields: forgotPasswordFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Handle form submission
  const handleSubmit = async (data: { email: string }) => {
    try {
      setLoading(true);
      setError(null);

      // Actual API call
      await forgotPassword({ email: data.email });

      // Update state
      setSentEmail(data.email);
      setEmailSent(true);

      toast({
        title: "Reset Link Sent",
        description: `Password reset instructions have been sent to ${data.email}`,
      });
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || // API error message
        (err instanceof Error
          ? err.message
          : "An error occurred while sending reset link");

      setError(errorMessage);

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle field changes
  const handleFieldChange = (fieldName: string, value: any) => {
    if (FormService.getFieldError(form, fieldName)) {
      form.clearErrors(fieldName);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.reset();
  };

  // Custom footer with send reset link button
  const renderCustomFooter = () => (
    <div className="space-y-4 mt-6">
      <Button
        type="submit"
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-medium text-base"
        disabled={loading}
      >
        {loading ? "Sending Reset Link..." : "Send Reset Link"}
      </Button>
    </div>
  );

  // Success message component
  const SuccessMessage = () => (
    <div className="space-y-4">
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="text-green-800 text-sm">
          <p className="font-medium mb-1">
            We've sent password reset instructions to {sentEmail}
          </p>
          <p>Please check your inbox.</p>
        </div>
      </div>

      <Button
        type="button"
        variant="link"
        className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
        onClick={() => navigate("/login")}
      >
        Return to login
      </Button>
    </div>
  );

  return (
    <div className="flex h-screen">
      {/* Left Section */}

      {/* <div className="w-1/2  flex">
        <img
          src="Smart360.png"
          alt="Login Background"
          className="w-full h-full"
        />
      </div> */}
        <ImageSection/>

      {/* Right side - Forgot Password form */}
      <div className="w-1/2 bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {!emailSent ? (
            <div className="flex flex-col gap-5">
              <div>
                <h2 className="page-title">Forgot Your Password?</h2>
                <p className="page-subtitle ">
                  Enter your email address to receive a reset link.
                </p>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Loading Display */}
              {loading && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm">
                  Sending reset link...
                </div>
              )}

              {/* Using DynamicForm component */}
              <div>
                <DynamicForm
                  fields={forgotPasswordFields}
                  form={form}
                  onSubmit={handleSubmit}
                  onReset={handleReset}
                  onFieldChange={handleFieldChange}
                  loading={loading}
                  config={{
                    showProgress: false,
                    layout: "vertical",
                    spacing: "none",
                    submitButtonText: "Send Reset Link",
                    showResetButton: false,
                    resetOnSubmit: false,
                    classes: {
                      form: "space-y-0",
                      fieldContainer: "",
                      buttonContainer: "hidden",
                      fieldGroup: "",
                    },
                  }}
                  renderCustomFooter={renderCustomFooter}
                  className=""
                  style={{
                    background: "transparent",
                    border: "none",
                    boxShadow: "none",
                  }}
                />
              </div>

              {/* Back to Login */}
              <div className="mt-6 text-center">
                <Button
                  type="button"
                  variant="link"
                  className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                  onClick={() => navigate("/login")}
                  disabled={loading}
                >
                  ← Back to Login
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Forgot Password
                </h2>
              </div>
              <SuccessMessage />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
